// ============================================================================
// SHARE SESSION DIALOG COMPONENT
// Modal for managing session access control lists
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { X, UserPlus, Trash2, Shield, Clock } from 'lucide-react';
import {
  SessionACLEntry,
  getSessionACL,
  addUserToACL,
  removeUserFromACL
} from '../services/sessionACL';

interface ShareSessionDialogProps {
  sessionId: string;
  sessionName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ShareSessionDialog: React.FC<ShareSessionDialogProps> = ({
  sessionId,
  sessionName,
  isOpen,
  onClose
}) => {
  const [acl, setAcl] = useState<SessionACLEntry[]>([]);
  const [ownerEmail, setOwnerEmail] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Add user form state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserAccessLevel, setNewUserAccessLevel] = useState<'read' | 'write'>('read');
  const [newUserExpires, setNewUserExpires] = useState<string>('');

  const loadACL = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getSessionACL(sessionId);
      setAcl(data.acl || []);
      setOwnerEmail(data.ownerEmail);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load sharing settings');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen) {
      loadACL();
    }
  }, [isOpen, sessionId, loadACL]);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newUserEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const result = await addUserToACL(sessionId, {
        email: newUserEmail.trim(),
        accessLevel: newUserAccessLevel,
        expiresAt: newUserExpires || undefined
      });

      setAcl(result.acl || []);
      setSuccess(`User ${newUserEmail} added successfully`);

      // Reset form
      setNewUserEmail('');
      setNewUserAccessLevel('read');
      setNewUserExpires('');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string, email: string) => {
    if (!window.confirm(`Remove ${email} from this session?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const result = await removeUserFromACL(sessionId, userId);
      setAcl(result.acl || []);
      setSuccess(`User ${email} removed successfully`);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000
        }}
      />

      {/* Dialog */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'hidden',
        zIndex: 1001
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              Share Session
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#868e96' }}>
              {sessionName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              color: '#868e96'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', maxHeight: 'calc(80vh - 160px)', overflow: 'auto' }}>
          {/* Error/Success Messages */}
          {error && (
            <div style={{
              padding: '12px',
              background: '#fff5f5',
              color: '#c92a2a',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{
              padding: '12px',
              background: '#ebfbee',
              color: '#2f9e44',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {success}
            </div>
          )}

          {/* Owner Info */}
          <div style={{
            padding: '12px',
            background: '#f8f9fa',
            borderRadius: '6px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Shield size={16} color="#228be6" />
            <span style={{ fontSize: '14px', color: '#495057' }}>
              Owner: <strong>{ownerEmail}</strong>
            </span>
          </div>

          {/* Add User Form */}
          <form onSubmit={handleAddUser} style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Add Collaborator
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                color: '#495057'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                color: '#495057'
              }}>
                Access Level
              </label>
              <select
                value={newUserAccessLevel}
                onChange={(e) => setNewUserAccessLevel(e.target.value as 'read' | 'write')}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="read">Read Only - Can view session</option>
                <option value="write">Read & Write - Can modify session</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px',
                color: '#495057'
              }}>
                Expiration Date (Optional)
              </label>
              <input
                type="datetime-local"
                value={newUserExpires}
                onChange={(e) => setNewUserExpires(e.target.value)}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !newUserEmail.trim()}
              style={{
                width: '100%',
                padding: '12px',
                background: loading || !newUserEmail.trim() ? '#adb5bd' : '#228be6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading || !newUserEmail.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <UserPlus size={16} />
              {loading ? 'Adding...' : 'Add User'}
            </button>
          </form>

          {/* Current Collaborators */}
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Current Collaborators ({acl.length})
            </h3>

            {acl.length === 0 ? (
              <p style={{ fontSize: '14px', color: '#868e96', fontStyle: 'italic' }}>
                No collaborators yet. Add users above to share this session.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {acl.map((entry) => (
                  <div
                    key={entry.userId}
                    style={{
                      padding: '12px',
                      background: isExpired(entry.expiresAt) ? '#fff5f5' : '#f8f9fa',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                        {entry.email}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          background: entry.accessLevel === 'write' ? '#228be6' : '#868e96',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {entry.accessLevel === 'write' ? 'Read & Write' : 'Read Only'}
                        </span>
                        {entry.expiresAt && (
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11px',
                            color: isExpired(entry.expiresAt) ? '#c92a2a' : '#868e96'
                          }}>
                            <Clock size={12} />
                            {isExpired(entry.expiresAt) ? 'Expired' : `Expires ${formatDate(entry.expiresAt)}`}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#868e96', marginTop: '4px' }}>
                        Added {formatDate(entry.grantedAt)}
                      </div>
                    </div>

                    <button
                      onClick={() => handleRemoveUser(entry.userId, entry.email)}
                      disabled={loading}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        padding: '8px',
                        color: '#c92a2a',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title="Remove user"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: '#495057',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};
